import React from 'react';
import { Mail, Users, GanttChartSquare, Heart, ClipboardList, Gift, LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Check } from 'lucide-react';

const ICONS: Record<string, LucideIcon> = {
  Mail,
  Users,
  GanttChartSquare,
  Heart,
  ClipboardList,
  Gift,
};

type FeatureCardBase = {
  title: string;
  desc: string;
  iconName?: keyof typeof ICONS | string;
};

type ImageModeProps = FeatureCardBase & {
  mode: 'image';
  imageSrc: string;
  alt: string;
  gradientClass?: string; // e.g. 'from-pink-100 to-purple-100'
  badges?: string[];
};

type IconModeProps = FeatureCardBase & {
  mode: 'icon';
};

type FeatureCardProps = ImageModeProps | IconModeProps;

export const FeatureCard: React.FC<FeatureCardProps> = (props) => {
  const Icon = props.iconName && ICONS[props.iconName as string] ? ICONS[props.iconName as string] : Mail;

  if (props.mode === 'icon') {
    const { title, desc } = props;
    return (
      <Card className="text-center bg-transparent border-wedding-pink/30 shadow-lg transition-transform duration-300 hover:scale-105 hover:shadow-xl">
        <CardHeader>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-wedding-pink/10">
            <Icon className="h-6 w-6 text-wedding-gold" />
          </div>
          <CardTitle className="mt-4 text-wedding-text-title">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p>{desc}</p>
        </CardContent>
      </Card>
    );
  }

  // mode === 'image'
  const { title, desc, imageSrc, alt, gradientClass, badges } = props;
  return (
    <Card className="group bg-white border-gray-200/80 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden">
      <div className={`aspect-video bg-gradient-to-br ${gradientClass ?? ''} relative overflow-hidden`}>
        <img src={imageSrc} alt={alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-500"></div>
        <div className="absolute top-4 left-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/90 backdrop-blur-sm">
            <Icon className="h-6 w-6 text-wedding-pink" />
          </div>
        </div>
      </div>
      <CardContent className="p-6">
        <CardTitle className="text-wedding-text-title text-xl font-semibold mb-3">{title}</CardTitle>
        <p className="text-gray-600 mb-4">{desc}</p>
        {badges && badges.length > 0 && (
          <div className="flex items-center gap-4 text-sm">
            {badges.map((b) => (
              <div key={b} className="flex items-center gap-1 text-wedding-pink">
                <Check className="h-4 w-4" />
                <span>{b}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

